import React from "react";
import {
  Box, Chip, ListItem, TextField, Typography, Button, Tooltip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { SimulationChangeType } from "../../interfaces/DTO/simulation";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { AdjustedProductType } from "../../interfaces/DTO/mislaka_xml_models";
import { formatSimulationChangeType } from "../../utils/boardTemplates";
import { colorSimulationChangeType } from "../../utils/boardTemplates";
import { formatCurrency, formatHandlerCode, formatPercentage } from "../../utils/namesFormater";
import { formatAdjustedProductType } from "../../utils/enumFormatter";
import { NO_FUND_SELECTED_LABEL } from "../../services/userTask/simulationChangeDetectionUtil";
import { getColorForPercentileNumber } from "../../utils/fundsFormatter";

export interface ChangeListItemData {
  policyNumber: string;
  contactId?: number;
  adjustedProductType?: AdjustedProductType;
  isExcluded?: boolean;
  changeTypes: SimulationChangeType[];
  handlerCode: string;
  originalHandlerCode: string | null;
  savingFee: number | null;
  allSavingFees: number[] | null;
  originalSavingFee: number | null;
  depositFee: number | null;
  originalDepositFee: number | null;
  fundChange: FundDataBasic[] | "NO_FUND_SELECTED" | null;
  removedFunds: FundDataBasic[];
  originalFunds: FundDataBasic[];
  depositInfo: string | null;
  fundsSum: number | null;
  originalFundsSum: number | null;
  userNote: string;
  autoHighlight: string;
  requestedExposureChanges: { label: string; oldValue: number; newValue: number; actualValue: number | null }[];
  sourceNote: string | null;
  sourceProductType: string | null;
  sourceHandlerCode: string | null;
  sourcePolicyNumber: string | null;
  sourceSavingFee: number[] | null;
  integrityWarning?: string;
}

interface Props {
  item: ChangeListItemData;
  noteValue: string;
  editingNoteKey: string | null;
  notesOnlyMode: boolean;
  listItemBgColor?: string;
  contactColorMap: Record<number, string>;
  showAdjustedProductType?: boolean;
  onNoteClick: (policyNumber: string, userNote: string) => void;
  onNoteChange: (policyNumber: string, value: string) => void;
  onNoteBlur: (policyNumber: string) => void;
  onNoteUpdate?: (policyNumber: string, note: string) => void;
  onNoteCancel: (policyNumber: string, userNote: string) => void;
}

const ChangeListItem: React.FC<Props> = ({
  item,
  noteValue,
  editingNoteKey,
  notesOnlyMode,
  listItemBgColor,
  contactColorMap,
  showAdjustedProductType = false,
  onNoteClick,
  onNoteChange,
  onNoteBlur,
  onNoteUpdate,
  onNoteCancel,
}) => {
  const isHandlerChanged = item.originalHandlerCode !== null && item.originalHandlerCode !== item.handlerCode;
  const handlerDisplay = isHandlerChanged
    ? `${formatHandlerCode(item.originalHandlerCode)} ← ${formatHandlerCode(item.handlerCode)}`
    : formatHandlerCode(item.handlerCode);

  const isFundChangeOnly = item.changeTypes.length === 1 && item.changeTypes[0] === SimulationChangeType.CHANGE_FUND;

  const origSavingFeeStr = item.allSavingFees && item.allSavingFees.length > 1
    ? item.allSavingFees.map(formatPercentage).join(", ")
    : item.originalSavingFee !== null ? formatPercentage(item.originalSavingFee) : null;
  const hasMultipleOrigFees = item.allSavingFees && item.allSavingFees.length > 1;
  const savingFeeDisplay = item.savingFee !== null
    ? (item.originalSavingFee !== null && item.originalSavingFee !== item.savingFee
        ? `${origSavingFeeStr} ← ${formatPercentage(item.savingFee)}`
        : hasMultipleOrigFees ? origSavingFeeStr : formatPercentage(item.savingFee))
    : item.originalSavingFee !== null && isHandlerChanged
      ? `${origSavingFeeStr} ← ?`
      : null;

  const depositFeeDisplay = item.depositFee !== null
    ? (item.originalDepositFee !== null && !(isFundChangeOnly && item.originalDepositFee === item.depositFee)
        ? `${formatPercentage(item.originalDepositFee)} ← ${formatPercentage(item.depositFee)}`
        : formatPercentage(item.depositFee))
    : item.originalDepositFee !== null && item.originalDepositFee > 0 && isHandlerChanged
      ? `${formatPercentage(item.originalDepositFee)} ← ?`
      : null;

  return (
    <ListItem
      sx={{
        borderRadius: 1,
        mb: 0.5,
        backgroundColor: listItemBgColor ?? "#fff",
        border: "1px solid #e0e0e0",
        ...(item.contactId && contactColorMap[item.contactId] ? { borderRight: `6px solid ${contactColorMap[item.contactId]}` } : {}),
        display: "grid",
        gridTemplateColumns: "5fr 5fr 2fr",
        alignItems: "start",
        gap: 1,
        py: 1,
        px: 2,
      }}
    >
      {/* Col 1: details */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {item.changeTypes.map((ct, i) => (
              <Chip
                key={i}
                label={formatSimulationChangeType(ct)}
                size="small"
                sx={{ backgroundColor: colorSimulationChangeType(ct), color: "#fff", fontWeight: 600, fontSize: "0.8rem", height: 24 }}
              />
            ))}
          </Box>
          {showAdjustedProductType && item.adjustedProductType != null && (
            <Typography variant="body2" color="text.secondary">
              {formatAdjustedProductType(item.adjustedProductType)}
            </Typography>
          )}
          {!item.changeTypes.includes(SimulationChangeType.OPEN_NEW) && (
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
              {item.policyNumber}
            </Typography>
          )}
          { item.sourceProductType&& (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, backgroundColor: "#f3e5f5", borderRadius: 1, px: 0.5 }}>
              <Typography variant="caption" color="text.secondary">מוצר מקור:</Typography>
              <Typography variant="caption" fontWeight={600}>{item.sourceProductType}</Typography>
            </Box>
          )}
          {item.sourceHandlerCode && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, backgroundColor: "#f3e5f5", borderRadius: 1, px: 0.5 }}>
              <Typography variant="caption" color="text.secondary">יצרן מקור:</Typography>
              <Typography variant="caption" fontWeight={600}>{item.sourceHandlerCode}</Typography>
            </Box>
          )}
          {item.sourcePolicyNumber && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, backgroundColor: "#f3e5f5", borderRadius: 1, px: 0.5 }}>
              <Typography variant="caption" color="text.secondary">פוליסה מקור:</Typography>
              <Typography variant="caption" fontWeight={600}>{item.sourcePolicyNumber}</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {handlerDisplay && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">יצרן:</Typography>
              <Typography variant="body2" sx={{ fontWeight: isHandlerChanged ? 600 : 400 }}>{handlerDisplay}</Typography>
            </Box>
          )}
          {savingFeeDisplay && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">דמ״נ צבירה:</Typography>
              <Typography variant="body2" fontWeight={600}>{savingFeeDisplay}</Typography>
            </Box>
          )}
          {depositFeeDisplay && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">דמ״נ הפקדה:</Typography>
              <Typography variant="body2" fontWeight={600}>{depositFeeDisplay}</Typography>
            </Box>
          )}
          {item.sourceSavingFee != null && item.sourceSavingFee.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, backgroundColor: "#f3e5f5", borderRadius: 1, px: 0.5 }}>
              <Typography variant="body2" color="text.secondary">דמ״נ צבירה מקור:</Typography>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {item.sourceSavingFee.map((fee, i) => (
                  <Typography key={i} variant="body2" fontWeight={600} dir="ltr">{formatPercentage(fee)}</Typography>
                ))}
              </Box>
            </Box>
          )}
          {(item.fundChange || item.originalFunds.length > 0) && (() => {
            const newFunds = Array.isArray(item.fundChange) ? item.fundChange as (FundDataBasic & { _amount?: number; _depositPct?: number })[] : [];
            const oldFunds = item.originalFunds as (FundDataBasic & { _amount?: number; _depositPct?: number })[];
            const oldTotal = oldFunds.reduce((s, f) => s + (f._amount ?? 0), 0);
            const newTotal = newFunds.reduce((s, f) => s + (f._amount ?? 0), 0);
            const showOldPct = oldFunds.length > 1 && oldTotal > 0;
            const showNewPct = newFunds.length > 1 && newTotal > 0;
            const showOldDeposit = oldFunds.some(f => (f._depositPct ?? 0) > 0);
            const showNewDeposit = newFunds.some(f => (f._depositPct ?? 0) > 0);
            const fundLabel = (f: FundDataBasic & { _amount?: number; _depositPct?: number }, showPct: boolean, total: number, showDeposit: boolean, bold: boolean) => {
              const parts: string[] = [`${f.fund_id} - ${f.fund_name}`];
              if (showPct) parts.push(`${((f._amount ?? 0) / total * 100).toFixed(1)}%`);
              if (showDeposit && (f._depositPct ?? 0) > 0) parts.push(`הפקדה: ${f._depositPct!.toFixed(1)}%`);
              const label = parts[0] + (parts.length > 1 ? ` (${parts.slice(1).join(", ")})` : "");
              return <Typography variant="body2" sx={bold ? { fontWeight: 600 } : { color: "text.secondary" }}>{label}</Typography>;
            };
            return (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>מסלול:</Typography>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, flexWrap: "wrap" }}>
                  {oldFunds.length > 0 && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                      {oldFunds.map((f) => {
                        const percentile = (f as any)?.percentile_6m_yield as number | undefined;
                        const color = percentile == null || isNaN(Number(percentile)) ? "#ccc" : getColorForPercentileNumber(Number(percentile));
                        return (
                          <Box key={f.fund_id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: color, flexShrink: 0 }} />
                            {fundLabel(f, showOldPct, oldTotal, showOldDeposit, false)}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                  {oldFunds.length > 0 && item.fundChange && item.fundChange !== "NO_FUND_SELECTED" && item.fundChange.length > 0 && (
                    <Typography variant="body2" color="text.secondary">←</Typography>
                  )}
                  {item.fundChange === "NO_FUND_SELECTED" ? (
                    <Typography variant="body2" sx={{ color: "error.main", fontWeight: 600 }}>{NO_FUND_SELECTED_LABEL}</Typography>
                  ) : newFunds.length > 0 ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                      {newFunds.map((f) => {
                        const percentile = f?.percentile_6m_yield as number | undefined;
                        const color = percentile == null || isNaN(Number(percentile)) ? "#ccc" : getColorForPercentileNumber(Number(percentile));
                        return (
                          <Box key={f.fund_id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: color, flexShrink: 0 }} />
                            {fundLabel(f, showNewPct, newTotal, showNewDeposit, true)}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : null}
                </Box>
              </Box>
            );
          })()}
          {item.depositInfo && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
              {item.depositInfo.split(" | ").map((part, i) => {
                const colonIdx = part.indexOf(": ");
                const label = colonIdx !== -1 ? part.slice(0, colonIdx) : null;
                const value = colonIdx !== -1 ? part.slice(colonIdx + 2) : part;
                return (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                    {label && <Typography variant="body2" color="text.secondary">{label}:</Typography>}
                    <Typography variant="body2" color="primary" fontWeight={600} component="span" dir="ltr" sx={{ backgroundColor: "#fff3e0", borderRadius: 1, px: 0.5 }}>{value}</Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>

      {/* Col 2: exposure + notes */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {item.requestedExposureChanges.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary">חשיפה מבוקשת:</Typography>
            {item.requestedExposureChanges.map((ec, i) => {
              const hasFundChange = Array.isArray(item.fundChange) && item.fundChange.length > 0;
              const isUnchanged = item.changeTypes.length === 1 && item.changeTypes[0] === SimulationChangeType.UNCHANGED;
              const showMatch = hasFundChange && !isUnchanged && ec.actualValue != null;
              const matched = showMatch && Math.abs(ec.actualValue! - ec.newValue) <= 10;
              if (showMatch) {
                return (
                  <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
                      {ec.label}: {ec.oldValue.toFixed(0)}% ← {ec.newValue.toFixed(0)}%
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 2 }}>
                      <Box sx={{ backgroundColor: matched ? "#2e7d32" : "#c62828", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, flexShrink: 0 }}>
                        {matched ? <CheckIcon sx={{ fontSize: 12, color: "#fff" }} /> : <CloseIcon sx={{ fontSize: 12, color: "#fff" }} />}
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: "0.72rem", fontWeight: 600 }}>
                        {ec.actualValue!.toFixed(0)}%
                      </Typography>
                    </Box>
                  </Box>
                );
              }
              return (
                <Chip
                  key={i}
                  label={`${ec.label}: ${ec.oldValue.toFixed(0)}% ← ${ec.newValue.toFixed(0)}%`}
                  size="small"
                  sx={{ backgroundColor: "#7b1fa2", color: "#fff", fontWeight: 600, fontSize: "0.75rem", height: 22 }}
                />
              );
            })}
          </Box>
        )}
        <Box sx={{ cursor: "pointer" }} onClick={() => onNoteClick(item.policyNumber, item.userNote)}>
          {editingNoteKey === item.policyNumber ? (
            <Box>
              <TextField
                autoFocus
                multiline
                rows={3}
                size="small"
                fullWidth
                value={noteValue}
                onChange={(e) => onNoteChange(item.policyNumber, e.target.value)}
                onBlur={() => onNoteBlur(item.policyNumber)}
                placeholder="הערה..."
                inputProps={{ dir: "rtl", style: { direction: "rtl", textAlign: "right", unicodeBidi: "plaintext" } }}
                onFocus={(e) => { const len = e.target.value.length; e.target.setSelectionRange(len, len); }}
                onClick={(e) => e.stopPropagation()}
              />
              {notesOnlyMode && (
                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  <Button size="small" variant="contained" onClick={() => onNoteUpdate?.(item.policyNumber, noteValue)}>עדכן</Button>
                  <Button size="small" variant="outlined" onClick={() => onNoteCancel(item.policyNumber, item.userNote)}>בטל</Button>
                </Box>
              )}
            </Box>
          ) : noteValue || item.autoHighlight ? (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
              <NoteAltOutlinedIcon fontSize="small" sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }} />
              <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {[noteValue, item.autoHighlight].filter(Boolean).join(" | ")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <NoteAltOutlinedIcon fontSize="small" sx={{ color: "action.disabled" }} />
              <Typography variant="caption" color="text.disabled">הוסף הערה</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Col 3: funds sum */}
      {item.fundsSum != null && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }}>
          {item.originalFundsSum != null && (
            <Typography variant="body2" color="text.disabled" sx={{ whiteSpace: "nowrap", textDecoration: "line-through" }}>
              {formatCurrency(item.originalFundsSum)}
            </Typography>
          )}
          <Typography variant="h6" fontWeight="bold" sx={{ whiteSpace: "nowrap", color: item.originalFundsSum != null ? (item.fundsSum! > item.originalFundsSum ? "success.main" : "error.main") : "text.secondary" }}>
            {formatCurrency(item.fundsSum)}
          </Typography>
          {item.integrityWarning && (
            <Tooltip arrow title={<span style={{ whiteSpace: "pre-line", direction: "rtl", display: "block", textAlign: "right" }}>{item.integrityWarning}</span>}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, cursor: "default" }}>
                <WarningAmberIcon sx={{ fontSize: "0.9rem", color: "error.main" }} />
                <Typography variant="caption" sx={{ color: "error.main", fontWeight: 700 }}>* התגלתה בעיה בשינויים בפוליסה - הקצאת המסלולים לא מסתכמת ב 100% מהסכום </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
      )}
    </ListItem>
  );
};

export default ChangeListItem;
